/**
 * PatternPad — 3x3 unlock pattern drawer / viewer.
 *
 * Props:
 *   value     : number[]  ordered sequence of dot indices (0..8)
 *   onChange  : (number[]) => void
 *   readOnly  : boolean   true = view-only (no input), shows path as SVG
 *   size      : number    pixel size of the square pad (default 180)
 *   testid    : string    optional data-testid prefix
 *
 * Indices layout:
 *   0 1 2
 *   3 4 5
 *   6 7 8
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';

const PatternPad = ({ value = [], onChange, readOnly = false, size = 180, testid = 'pattern-pad' }) => {
  const [drawing, setDrawing] = useState(false);
  const [hoverPath, setHoverPath] = useState([]);
  const padRef = useRef(null);

  const path = drawing ? hoverPath : (Array.isArray(value) ? value : []);
  const padding = size * 0.12;
  const cell = (size - padding * 2) / 2; // distance between adjacent dot centres
  const dotR = size * 0.06;

  const dotCenter = useMemo(() => {
    const out = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        out.push({ x: padding + c * cell, y: padding + r * cell });
      }
    }
    return out;
  }, [padding, cell]);

  const hitTest = useCallback((clientX, clientY) => {
    const el = padRef.current;
    if (!el) return -1;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const tol = dotR * 1.8;
    for (let i = 0; i < 9; i++) {
      const dx = dotCenter[i].x - x;
      const dy = dotCenter[i].y - y;
      if (dx * dx + dy * dy <= tol * tol) return i;
    }
    return -1;
  }, [dotCenter, dotR]);

  const beginAt = (idx) => {
    setHoverPath([idx]);
    setDrawing(true);
  };

  const extendTo = (idx) => {
    setHoverPath(prev => (prev.includes(idx) ? prev : [...prev, idx]));
  };

  const endDraw = () => {
    if (!drawing) return;
    setDrawing(false);
    if (onChange) onChange(hoverPath);
  };

  const handlePointerDown = (e) => {
    if (readOnly) return;
    const idx = hitTest(e.clientX, e.clientY);
    if (idx >= 0) {
      e.preventDefault();
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
      beginAt(idx);
    }
  };

  const handlePointerMove = (e) => {
    if (!drawing || readOnly) return;
    const idx = hitTest(e.clientX, e.clientY);
    if (idx >= 0) extendTo(idx);
  };

  const handlePointerUp = () => {
    if (readOnly) return;
    endDraw();
  };

  const clear = () => {
    setHoverPath([]);
    setDrawing(false);
    if (onChange) onChange([]);
  };

  return (
    <div className="inline-block" data-testid={testid}>
      <div
        ref={padRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className={`relative select-none ${readOnly ? '' : 'touch-none cursor-crosshair'} bg-slate-50 border border-slate-200 rounded-lg`}
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} className="absolute inset-0 pointer-events-none">
          {/* connecting lines */}
          {path.length > 1 && path.slice(0, -1).map((idx, i) => {
            const a = dotCenter[idx];
            const b = dotCenter[path[i + 1]];
            return (
              <line
                key={`${idx}-${path[i + 1]}-${i}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke="#1D3C6E" strokeWidth={3} strokeLinecap="round"
              />
            );
          })}
          {/* dots */}
          {dotCenter.map((p, i) => {
            const order = path.indexOf(i);
            const active = order >= 0;
            return (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r={dotR}
                  fill={active ? '#1D3C6E' : '#cbd5e1'}
                  stroke={active ? '#AFBD20' : 'transparent'}
                  strokeWidth={2}
                />
                {active && (
                  <text x={p.x} y={p.y + 3.5} textAnchor="middle" fontSize={dotR * 1.1}
                    fontWeight="700" fill="white" pointerEvents="none">
                    {order + 1}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {!readOnly && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500" style={{ width: size }}>
          <span data-testid={`${testid}-summary`}>
            {path.length > 0 ? `Secuencia: ${path.map(i => i + 1).join(' → ')}` : 'Dibuja el patrón'}
          </span>
          {path.length > 0 && (
            <button type="button" onClick={clear} className="text-rose-600 hover:underline" data-testid={`${testid}-clear`}>
              Limpiar
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PatternPad;
